import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { type ExportFormat, KEYS } from '~shared/api/query-keys';

import {
  type AttendanceReportData,
  type AttendanceReportRangeParams,
} from './reporting-attendance-report.types';

const LOAD_ERROR_MESSAGE =
  'We could not load the attendance report. Please try again.';
const DOWNLOAD_ERROR_MESSAGE =
  'We could not download the attendance report. Please try again.';

const toRangeQueryParams = (range: AttendanceReportRangeParams) => ({
  startDate: range.startDate ?? undefined,
  endDate: range.endDate ?? undefined,
});

export const reportingAttendanceReportAPI = async (
  format: ExportFormat = KEYS.EXPORT_FORMAT.CSV,
  range: AttendanceReportRangeParams = {},
) => {
  try {
    const response = await baseAPI.get<string>('reporting/attendance-report', {
      params: { format, ...toRangeQueryParams(range) },
      responseType: 'text',
    });
    return response.data;
  } catch (error) {
    console.error('Failed to download the attendance report', { error });
    throw new Error(DOWNLOAD_ERROR_MESSAGE);
  }
};

export const fetchAttendanceReport = async (
  range: AttendanceReportRangeParams = {},
): Promise<AttendanceReportData> => {
  try {
    const response = await baseAPI.get<AttendanceReportData>(
      'reporting/attendance-report',
      {
        params: {
          format: KEYS.EXPORT_FORMAT.JSON,
          ...toRangeQueryParams(range),
        },
      },
    );
    return {
      studioName: response.data.studioName,
      absenceRange: response.data.absenceRange,
      rows: response.data.rows,
    };
  } catch (error) {
    console.error('Failed to load the attendance report', { error });
    throw new Error(LOAD_ERROR_MESSAGE);
  }
};

export const useAttendanceReport = (
  range: AttendanceReportRangeParams = {},
) => {
  return useQuery({
    queryKey: [
      KEYS.ATTENDANCE_REPORT,
      KEYS.EXPORT_FORMAT.JSON,
      range.startDate ?? '',
      range.endDate ?? '',
    ],
    queryFn: () => fetchAttendanceReport(range),
  });
};
